import React from "react";
import { Check, X, Minus } from "@phosphor-icons/react";
import { useI18n } from "@/context/LangContext";

/**
 * A short honest comparison strip on every product page.
 * Compares Maxine against a "traditional global ERP suite" pattern.
 * No brand names are used — the copy is deliberately generic and defensible.
 */

const ROWS = [
  {
    label: "Time to go-live",
    maxine: "30 days (avg.)",
    trad: "12–18 months",
    us: "good",
    them: "bad",
  },
  {
    label: "Pricing model",
    maxine: "Monthly · yearly · outright — fixed",
    trad: "Per-seat + implementation + change orders",
    us: "good",
    them: "bad",
  },
  {
    label: "Region focus",
    maxine: "UAE · India · Africa · EU (data-residency by contract)",
    trad: "Global — regional compliance sold as bolt-on",
    us: "good",
    them: "mid",
  },
  {
    label: "Local compliance (VAT · GST · WPS · KHDA · CBN · FTA · GDPR / EHDS)",
    maxine: "Built-in per country",
    trad: "Partner-delivered add-ons",
    us: "good",
    them: "mid",
  },
  {
    label: "Support model",
    maxine: "24×7 desk · named partner manager · WhatsApp allowed",
    trad: "Ticket-based · SLA within business hours",
    us: "good",
    them: "mid",
  },
  {
    label: "White-label / reseller path",
    maxine: "Yes — 25–55% revenue share",
    trad: "Rarely offered outside Tier-1 SIs",
    us: "good",
    them: "bad",
  },
  {
    label: "Customisation depth",
    maxine: "Source-level, per-tenant",
    trad: "Config-level, upgrade-safe only",
    us: "good",
    them: "mid",
  },
];

function Cell({ tone }) {
  if (tone === "good") return <Check size={16} weight="bold" className="text-emerald-600" />;
  if (tone === "bad") return <X size={16} weight="bold" className="text-neutral-400" />;
  return <Minus size={16} weight="bold" className="text-amber-500" />;
}

export default function ProductComparison({ productTitle }) {
  const { t } = useI18n();
  return (
    <section data-testid="product-comparison" className="mt-14 border border-[color:var(--line)] bg-white overflow-hidden">
      <div className="border-b border-[color:var(--line)] px-6 py-4">
        <div className="eyebrow">{t("cmp.eyebrow")}</div>
        <h3 className="mt-1 font-display font-bold text-lg md:text-2xl leading-tight">
          {t("cmp.title", { product: productTitle })}
        </h3>
        <p className="mt-1 text-xs md:text-sm text-neutral-600 max-w-2xl">
          {t("cmp.sub")}
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-[color:var(--paper-alt)]">
            <tr>
              <th className="text-left font-mono text-[10px] uppercase tracking-widest text-neutral-500 px-6 py-3 w-1/3">{t("cmp.col.criterion")}</th>
              <th className="text-left font-mono text-[10px] uppercase tracking-widest text-[color:var(--brand)] px-6 py-3 border-l border-[color:var(--line)]">
                {t("cmp.col.us")}
              </th>
              <th className="text-left font-mono text-[10px] uppercase tracking-widest text-neutral-500 px-6 py-3 border-l border-[color:var(--line)]">
                {t("cmp.col.them")}
              </th>
            </tr>
          </thead>
          <tbody>
            {ROWS.map((r) => (
              <tr key={r.label} className="border-t border-[color:var(--line)]">
                <td className="px-6 py-3 font-display font-semibold text-neutral-800 align-top">{r.label}</td>
                <td className="px-6 py-3 border-l border-[color:var(--line)] align-top">
                  <div className="flex items-start gap-2">
                    <Cell tone={r.us} />
                    <span className="text-neutral-800">{r.maxine}</span>
                  </div>
                </td>
                <td className="px-6 py-3 border-l border-[color:var(--line)] align-top">
                  <div className="flex items-start gap-2">
                    <Cell tone={r.them} />
                    <span className="text-neutral-600">{r.trad}</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="border-t border-[color:var(--line)] px-6 py-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
        Comparison reflects Maxine&apos;s standard offer vs. common patterns in Tier-1 global ERP contracts (2026).
      </div>
    </section>
  );
}
